import { defineStore } from 'pinia'
import { getScheduleMedia } from '@/api/mediaSchedules'
import { updateMedia, deleteMedia } from '@/api/gmb/media'
import { locationDateKey } from '@/vue_widgets/media_schedules/helpers/timeline'
import { DEFAULT_ERROR_MESSAGE } from '@/common/constants'

// Page-scoped store for /media_schedules/:id. `schedule` mirrors
// MediaScheduleSerializer (hydrated from the styxie payload); media is kept
// pre-grouped as { locationId => { dateKey(YYYYMMDD) => [media] } } for O(1)
// cell lookups, extra date windows are lazy-loaded while scrolling.

const STATE = () => ({
  schedule: null,
  mediaByLocationDate: {},
  loadedDateRanges: [],
  dataTransfer: null,
  removingImage: false,
  actionError: null
})

export const useMediaScheduleShowStore = defineStore('mediaScheduleShow', {
  state: STATE,
  getters: {
    getMediaForCell: (state) => (locationId, dateKey) =>
      state.mediaByLocationDate[locationId]?.[dateKey] || [],
    isDateRangeLoaded: (state) => (startDate, endDate) =>
      state.loadedDateRanges.some((range) => range.start <= startDate && range.end >= endDate)
  },
  actions: {
    initialize(payload) {
      const { mediaByLocationDate, visibleDateRange, ...schedule } = payload
      this.schedule = schedule
      this.mediaByLocationDate = mediaByLocationDate || {}
      this.loadedDateRanges = visibleDateRange ?
        [{ start: visibleDateRange.start, end: visibleDateRange.end }] :
        []
    },

    updateSchedule(payload) {
      // Media is already added incrementally via addMediaToGroup during
      // processing — a cable payload carries only the visible window, so
      // taking its media would drop already-loaded ranges.
      const { mediaByLocationDate: _dropped, ...schedule } = payload
      this.schedule = schedule
    },

    updateScheduleProperty(payload) {
      Object.assign(this.schedule, payload)
    },

    async fetchMediaForDateRange({ startDate, endDate }) {
      if (this.isDateRangeLoaded(startDate, endDate)) return

      // Recorded before the response so a window asked for again mid-flight
      // doesn't hit the server twice.
      const range = { start: startDate, end: endDate }
      this.loadedDateRanges.push(range)
      try {
        const res = await getScheduleMedia(this.schedule.obfuscatedId, { startDate, endDate })
        this.mergeMediaByLocationDate(res.data.mediaByLocationDate)
      } catch (err) {
        this.loadedDateRanges.splice(this.loadedDateRanges.indexOf(range), 1)
        this.actionError = `Failed to load media: ${err.response?.data?.error || DEFAULT_ERROR_MESSAGE}`
      }
    },

    // Drag&drop reschedule within a location row: optimistic spinner
    // placeholder at the target cell, rollback on failure.
    async rescheduleMedia(newDate) {
      const { locationId, media } = this.dataTransfer
      // Backend computes dateKey as scheduled_at.in_time_zone(location_tz).to_date
      const location = this.schedule.locations.find(({ id }) => id === locationId)
      const newDateKey = locationDateKey(newDate, location?.timeZoneOffset || 0)
      const placeholderId = `${media.id}_transfering`

      this.updateMediaInGroup({ locationId, mediaId: media.id, properties: { transfering: true } })
      this.addMediaToGroup({
        locationId,
        dateKey: newDateKey,
        media: {
          id: placeholderId, scheduledAt: newDate, spinner: true, locationId: media.locationId
        }
      })

      try {
        await updateMedia(locationId, media.id, { gmbMedia: { scheduledAt: newDate } })

        this.removeMediaFromGroup({ locationId, mediaId: media.id })
        this.removeMediaFromGroup({ locationId, mediaId: placeholderId })
        this.addMediaToGroup({
          locationId,
          dateKey: newDateKey,
          media: { ...media, scheduledAt: newDate, transfering: false }
        })
      } catch (err) {
        this.removeMediaFromGroup({ locationId, mediaId: placeholderId })
        this.updateMediaInGroup({ locationId, mediaId: media.id, properties: { transfering: false } })
        this.actionError = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
      } finally {
        this.dataTransfer = null
      }
    },

    // Drop onto the backdrop: delete the dragged media.
    async removeMedia() {
      this.removingImage = false
      if (!this.dataTransfer) return

      const { locationId, media } = this.dataTransfer
      this.updateMediaInGroup({ locationId, mediaId: media.id, properties: { transfering: true } })

      try {
        await deleteMedia(locationId, media.id)
        this.removeMediaFromGroup({ locationId, mediaId: media.id })
        this.updateScheduleProperty({ mediaCount: this.schedule.mediaCount - 1 })
      } catch (err) {
        this.updateMediaInGroup({ locationId, mediaId: media.id, properties: { transfering: false } })
        if (err.response?.status !== 403) {
          this.actionError = err.response?.data?.errors || DEFAULT_ERROR_MESSAGE
        }
      } finally {
        this.dataTransfer = null
      }
    },

    mergeMediaByLocationDate(newData) {
      if (!newData) return

      Object.keys(newData).forEach((locationId) => {
        this.mediaByLocationDate[locationId] ||= {}
        Object.assign(this.mediaByLocationDate[locationId], newData[locationId])
      })
    },

    addMediaToGroup({ locationId, dateKey, media }) {
      this.mediaByLocationDate[locationId] ||= {}
      this.mediaByLocationDate[locationId][dateKey] ||= []
      this.mediaByLocationDate[locationId][dateKey].push(media)
    },

    removeMediaFromGroup({ locationId, mediaId }) {
      const locationData = this.mediaByLocationDate[locationId]
      if (!locationData) return

      Object.keys(locationData).forEach((dateKey) => {
        const index = locationData[dateKey].findIndex((m) => m.id === mediaId)
        if (index !== -1) locationData[dateKey].splice(index, 1)
      })
    },

    updateMediaInGroup({ locationId, mediaId, properties }) {
      const locationData = this.mediaByLocationDate[locationId]
      if (!locationData) return

      Object.keys(locationData).forEach((dateKey) => {
        const media = locationData[dateKey].find((m) => m.id === mediaId)
        if (media) Object.assign(media, properties)
      })
    }
  }
})
